import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image as ImageIcon } from "lucide-react";
import { alvoTour } from "../../tour";
import { miniaturaDe } from "../../../utils/miniatura";
import { BRL, INT, MIL, PCT, dataLocal } from "../formato";
import { CORES, Avatar, Donut, Ranking } from "./Graficos";
import { LinhasCarregando, BlocoCarregando } from "./Carregando";

const LIMITE = 10;
const dataBR = (iso) => dataLocal(iso).toLocaleDateString("pt-BR");
/** "1 pedido", "2 pedidos" (antes saía "1 pedidos"). */
const nPedidos = (n) => `${INT(n)} ${Math.round(Number(n || 0)) === 1 ? "pedido" : "pedidos"}`;

/** Quantidade com unidade: produto por quilo/litro vem com `unidade`. */
function qtd(v, unidade) {
  if (unidade === "kg" || unidade === "L") {
    return `${Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ${unidade}`;
  }
  return `${INT(v)} un.`;
}

/** Junta o que passa do limite numa linha "Outros N ...". */
function comResto(lista, n, rotulo) {
  if (lista.length <= n) return lista;
  const resto = lista.slice(n);
  return [...lista.slice(0, n), {
    n: `Outr${rotulo.endsWith("a") || rotulo.endsWith("as") ? "as" : "os"} ${resto.length} ${rotulo}`,
    v: resto.reduce((a, i) => a + i.v, 0), rest: true,
    q: resto.some((i) => i.q) ? "" : undefined, extra: resto.reduce((a, i) => a + (i.extra || 0), 0),
  }];
}

export default function Resumo({ resumo }) {
  const periodo = resumo ? `Últimos 3 meses: ${dataBR(resumo.periodo.inicio)} a ${dataBR(resumo.periodo.fim)}.` : "Últimos 3 meses.";
  return (
    <section ref={alvoTour("vg-resumo")} className="vg-section">
      <div className="vg-section-head">
        <h2>Resumo</h2>
        <p>Clientes, produtos e operação. {periodo}</p>
      </div>
      <div className="vg-cols-2">
        <Clientes r={resumo} />
        <Produtos r={resumo} />
        <Formas r={resumo} />
        <Modulos r={resumo} />
        <Entregadores r={resumo} />
        <Categorias r={resumo} />
      </div>
    </section>
  );
}

function Cartao({ titulo, sub, direita, children }) {
  return (
    <article className="vg-card">
      <div className="vg-card-head">
        <div><h3>{titulo}</h3>{sub && <p>{sub}</p>}</div>
        {direita}
      </div>
      {children}
    </article>
  );
}

/** Nome (com avatar/foto/cor) e uma 2ª linha miúda; corta com "…" em vez de alargar a tabela. */
function Nome({ antes, nome, linha2 }) {
  return (
    <div className="vg-nm">
      {antes}
      <div className="vg-nm-tx"><span title={nome}>{nome}</span>{linha2 && <small className="l2">{linha2}</small>}</div>
    </div>
  );
}

function Clientes({ r }) {
  const navigate = useNavigate();
  const lista = r?.clientes?.slice(0, LIMITE) || [];
  return (
    <Cartao titulo="Clientes que mais compraram" direita={<a className="vg-sub" onClick={() => navigate("/admin/clientes-fornecedores/clientes")}>Ver todos</a>}>
      {!r ? <LinhasCarregando /> : !lista.length ? <div className="vg-vazio">Nenhum cliente identificado no período.</div> : (
        <>
          <BaseClientes b={r.base_clientes} />
          <table className="vg-tbl">
            <thead><tr><th /><th>Cliente</th><th className="r">Total</th></tr></thead>
            <tbody>
              {lista.map((c, k) => (
                <tr key={c.id || k}>
                  <td className="i">{k + 1}</td>
                  <td className="nm"><Nome antes={<Avatar nome={c.nome} k={k} />} nome={c.nome}
                    linha2={`${INT(c.quantidade_pedidos)} ${c.quantidade_pedidos === 1 ? "pedido" : "pedidos"} · ticket ${BRL(c.ticket_medio)}`} /></td>
                  <td className="r b">{BRL(c.valor_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Cartao>
  );
}

/** Clientes novos × que voltaram no período (só identificados). */
function BaseClientes({ b }) {
  if (!b?.total) return null;
  const pNovos = (b.novos / b.total) * 100;
  const fat = b.faturamento_novos + b.faturamento_voltaram;
  return (
    <div className="vg-base">
      <div className="vg-base-nums">
        <div><b>{INT(b.total)}</b><span>clientes compraram</span></div>
        <div><b className="novo">{INT(b.novos)}</b><span>novos · {PCT(pNovos)}</span></div>
        <div><b className="volta">{INT(b.voltaram)}</b><span>voltaram · {PCT(100 - pNovos)}</span></div>
      </div>
      <div className="vg-base-bar" aria-hidden="true"><i style={{ width: `${pNovos}%` }} /></div>
      <p className="vg-base-txt">
        Quem voltou fez <b>{PCT(fat ? (b.faturamento_voltaram / fat) * 100 : 0)}</b> do faturamento dos clientes identificados.
        {b.novos > 0 && <> Dos novos, <b>{PCT((b.novos_que_voltaram / b.novos) * 100)}</b> já compraram de novo.</>}
      </p>
    </div>
  );
}

function Produtos({ r }) {
  const navigate = useNavigate();
  const lista = r?.produtos?.slice(0, LIMITE) || [];
  return (
    <Cartao titulo="Produtos mais vendidos" direita={<a className="vg-sub" onClick={() => navigate("/admin/vendas/relatorio-produtos")}>Ver todos</a>}>
      {!r ? <LinhasCarregando /> : !lista.length ? <div className="vg-vazio">Nenhum produto vendido no período.</div> : (
        <table className="vg-tbl">
          <thead><tr><th /><th>Produto</th><th className="r">Qtd.</th><th className="r">Total</th></tr></thead>
          <tbody>
            {lista.map((p, k) => (
              <tr key={p.id || k}>
                <td className="i">{k + 1}</td>
                <td className="nm"><Nome nome={p.nome} linha2={p.categoria}
                  antes={<span className="vg-pv">{p.foto ? <img src={miniaturaDe(p.foto, 128)} alt="" loading="lazy" /> : <ImageIcon className="w-[15px] h-[15px]" />}</span>} /></td>
                <td className="r">{qtd(p.quantidade, p.unidade)}</td>
                <td className="r b">{BRL(p.valor_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Cartao>
  );
}

function Formas({ r }) {
  const [ativo, setAtivo] = useState(null);
  const lista = r?.formas || [];
  const tot = lista.reduce((a, f) => ({ total: a.total + f.total, pedidos: a.pedidos + f.pedidos, taxa: a.taxa + f.taxa }), { total: 0, pedidos: 0, taxa: 0 });
  return (
    <Cartao titulo="Formas de pagamento" sub="Faturamento por forma de pagamento no período.">
      {!r ? <BlocoCarregando altura={190} /> : !lista.length ? <div className="vg-vazio">Nenhuma venda no período.</div> : (
        <>
          <Donut itens={lista.map((f, k) => ({ n: f.nome, v: f.total, c: CORES[k % CORES.length] }))} ativo={ativo} onAtivo={setAtivo}
            rotulo="Total" valorCentro={`R$ ${MIL(tot.total)}`} fmt={BRL} />
          <div className="vg-tbl-wrap">
            <table className="vg-tbl empilha">
              <thead><tr><th>Forma</th><th className="r">Total</th><th className="r">Taxa paga</th></tr></thead>
              <tbody>
                {lista.map((f, k) => (
                  <tr key={f.nome} data-k={k} className={ativo === k ? "on" : ""} onMouseEnter={() => setAtivo(k)} onMouseLeave={() => setAtivo(null)}>
                    <td className="nm"><Nome antes={<i className="vg-sw" style={{ background: CORES[k % CORES.length] }} />} nome={f.nome}
                      linha2={`${PCT((f.total / (tot.total || 1)) * 100)} · ${nPedidos(f.pedidos)}`} /></td>
                    <td className="r b">{BRL(f.total)}<small className="l2">ticket {BRL(f.ticket_medio)}</small></td>
                    <td className="r extra" data-rot="Taxa paga">{BRL(f.taxa)}<small className="l2">{PCT(f.total ? (f.taxa / f.total) * 100 : 0)} do total</small></td>
                  </tr>
                ))}
                <tr className="total">
                  <td className="nm"><Nome nome="Total" linha2={`100% · ${nPedidos(tot.pedidos)}`} /></td>
                  <td className="r">{BRL(tot.total)}<small className="l2">ticket {BRL(tot.pedidos ? tot.total / tot.pedidos : 0)}</small></td>
                  <td className="r extra" data-rot="Taxa paga">{BRL(tot.taxa)}<small className="l2">{PCT(tot.total ? (tot.taxa / tot.total) * 100 : 0)} do total</small></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="vg-note">Taxa paga = taxa configurada em Financeiro → Formas de Pagamento, a vigente na data de cada venda. Forma sem taxa configurada aparece com R$ 0,00. Venda dividida em duas formas conta nas duas.</p>
        </>
      )}
    </Cartao>
  );
}

function Modulos({ r }) {
  const [ativo, setAtivo] = useState(null);
  const lista = r?.modulos || [];
  const tPed = lista.reduce((a, m) => a + m.pedidos, 0), tFat = lista.reduce((a, m) => a + m.faturamento, 0), tCanc = lista.reduce((a, m) => a + m.cancelados, 0);
  const comTempo = lista.filter((m) => m.tempo_medio_min != null);
  const tTempo = comTempo.length ? Math.round(comTempo.reduce((a, m) => a + m.tempo_medio_min * m.pedidos, 0) / (comTempo.reduce((a, m) => a + m.pedidos, 0) || 1)) : null;
  const taxa = (c, p) => PCT(c + p ? (c / (c + p)) * 100 : 0);
  return (
    <Cartao titulo="Módulos de venda" sub="Pedidos por módulo no período. Módulos sem venda não aparecem.">
      {!r ? <BlocoCarregando altura={190} /> : !lista.length ? <div className="vg-vazio">Nenhuma venda no período.</div> : (
        <>
          <Donut itens={lista.map((m, k) => ({ n: m.nome, v: m.pedidos, c: CORES[k % CORES.length] }))} ativo={ativo} onAtivo={setAtivo}
            rotulo="Pedidos" valorCentro={INT(tPed)} fmt={nPedidos} />
          <div className="vg-tbl-wrap">
            <table className="vg-tbl empilha">
              <thead><tr><th>Módulo</th><th className="r">Faturamento</th><th className="r">Tempo</th></tr></thead>
              <tbody>
                {lista.map((m, k) => (
                  <tr key={m.key} data-k={k} className={ativo === k ? "on" : ""} onMouseEnter={() => setAtivo(k)} onMouseLeave={() => setAtivo(null)}>
                    <td className="nm"><Nome antes={<i className="vg-sw" style={{ background: CORES[k % CORES.length] }} />} nome={m.nome}
                      linha2={`${PCT(tPed ? (m.pedidos / tPed) * 100 : 0)} · ${nPedidos(m.pedidos)}`} /></td>
                    <td className="r b">{BRL(m.faturamento)}<small className="l2">ticket {BRL(m.ticket_medio)}</small></td>
                    <td className="r extra" data-rot="Tempo">{m.tempo_medio_min == null ? "—" : `${m.tempo_medio_min} min`}
                      <small className="l2">{INT(m.cancelados)} cancel. · {taxa(m.cancelados, m.pedidos)}</small></td>
                  </tr>
                ))}
                <tr className="total">
                  <td className="nm"><Nome nome="Total" linha2={`100% · ${nPedidos(tPed)}`} /></td>
                  <td className="r">{BRL(tFat)}<small className="l2">ticket {BRL(tPed ? tFat / tPed : 0)}</small></td>
                  <td className="r extra" data-rot="Tempo">{tTempo == null ? "—" : `${tTempo} min`}<small className="l2">{INT(tCanc)} cancel. · {taxa(tCanc, tPed)}</small></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Cartao>
  );
}

function Entregadores({ r }) {
  const lista = r?.entregadores || [];
  const tEnt = lista.reduce((a, e) => a + e.entregas, 0), tVal = lista.reduce((a, e) => a + e.valor, 0);
  const itens = comResto(lista.map((e) => ({ n: e.nome, s: e.empresa || undefined, v: e.entregas, extra: e.valor })), LIMITE, "entregadores");
  return (
    <Cartao titulo="Entregadores" sub="Entregas realizadas no período."
      direita={r && lista.length ? <span className="vg-sub">{INT(tEnt)} entregas · {lista.length} entregadores · {BRL(tVal)}</span> : null}>
      {!r ? <LinhasCarregando /> : !lista.length ? <div className="vg-vazio">Nenhuma entrega no período.</div> : (
        <>
          <div className="vg-ranking-head ent"><span /><span>Entregador</span><span /><span className="r">Entregas</span><span className="r">Transportado</span></div>
          <Ranking classe="ent" itens={itens} total={tEnt} avatar fmt={INT} segundo={(it) => BRL(it.extra)} />
        </>
      )}
    </Cartao>
  );
}

function Categorias({ r }) {
  const lista = r?.categorias || [];
  const total = lista.reduce((a, c) => a + Number(c.valor_total || 0), 0);
  const q = (c) => [c.quantidade ? `${INT(c.quantidade)} un.` : null, c.quantidade_kg ? `${Number(c.quantidade_kg).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} kg` : null,
    c.quantidade_l ? `${Number(c.quantidade_l).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} L` : null].filter(Boolean).join(" + ");
  const itens = comResto(lista.map((c) => ({ n: c.name || "Sem categoria", v: Number(c.valor_total || 0), q: q(c) })), LIMITE, "categorias");
  return (
    <Cartao titulo="Categorias mais vendidas" sub="Faturamento por categoria no período."
      direita={r && lista.length ? <span className="vg-sub">Total {BRL(total)}</span> : null}>
      {!r ? <LinhasCarregando /> : !lista.length ? <div className="vg-vazio">Nenhuma venda no período.</div> : (
        <>
          <div className="vg-ranking-head"><span /><span>Categoria</span><span /><span className="r">Faturamento</span></div>
          <Ranking itens={itens} total={total} fmt={BRL} />
        </>
      )}
    </Cartao>
  );
}
